import UUID, { uuid } from '@ax2/common/model/UUID';
import QuestionType from '@ax2/common/model/QuestionType';
import QuestionSchema from '@ax2/common/model/schemas/QuestionSchema';
import AnswerType from '@ax2/common/model/AnswerType';
import AnswerColumnSchema from '@ax2/common/model/schemas/AnswerColumnSchema';
import AnswerRowSchema from '@ax2/common/model/schemas/AnswerRowSchema';
import { GridRequiredStrategy } from '@ax2/common/model/schemas/GridRequiredOptions';
import TemplateContent from '@ax2/common/model/schemas/TemplateContent';
import _ from 'lodash';

export function createQuestionSchema(
  id: UUID,
  order: number,
  qType: QuestionType
): QuestionSchema | null {
  switch (qType) {
    case QuestionType.Empty:
      return {
        id: id,
        questionTemplateId: null,
        order: order,
        questionType: qType,
        isRequired: true,
        imageUrl: null,
        isLocked: false,
      };
    case QuestionType.OpenEndedNumeric:
    case QuestionType.OpenEndedText:
      return {
        id: id,
        questionTemplateId: null,
        order: order,
        questionType: qType,
        isRequired: true,
        imageUrl: null,
        isLocked: false,
        title: null,
        content: getNewTemplateContent(null),
        description: null,
      };
    case QuestionType.CheckboxGrid:
      return {
        id: id,
        questionTemplateId: null,
        order: order,
        questionType: qType,
        isExclusivePerRow: null,
        isRequired: true,
        imageUrl: null,
        requiredOptions: {
          strategy: GridRequiredStrategy.AtLeastOnePerColumn,
        },
        isLocked: false,
        randomizeColumn: false,
        randomizeRow: false,
        title: null,
        content: getNewTemplateContent(null),
        description: null,
        columns: [createAnswerSchema(null, 0), createAnswerSchema(null, 1)],
        rows: [createAnswerSchema(null, 0), createAnswerSchema(null, 1)],
      };
    case QuestionType.RadioButtonGrid:
      return {
        id: id,
        questionTemplateId: null,
        order: order,
        questionType: qType,
        isExclusivePerRow: false,
        isRequired: true,
        imageUrl: null,
        requiredOptions: {
          strategy: GridRequiredStrategy.OnePerColumn,
        },
        isLocked: false,
        randomizeColumn: false,
        randomizeRow: false,
        title: null,
        content: getNewTemplateContent(null),
        description: null,
        columns: [createAnswerSchema(null, 0), createAnswerSchema(null, 1)],
        rows: [createAnswerSchema(null, 0), createAnswerSchema(null, 1)],
      };
    case QuestionType.RadioButtonGridRow:
      return {
        id: id,
        questionTemplateId: null,
        order: order,
        questionType: qType,
        isExclusivePerRow: false,
        isRequired: true,
        imageUrl: null,
        requiredOptions: {
          strategy: GridRequiredStrategy.OnePerRow,
        },
        isLocked: false,
        randomizeColumn: false,
        randomizeRow: false,
        title: null,
        content: getNewTemplateContent(null),
        description: null,
        columns: [createAnswerSchema(null, 0), createAnswerSchema(null, 1)],
        rows: [createAnswerSchema(null, 0), createAnswerSchema(null, 1)],
      };

    case QuestionType.RatingList:
      return {
        id: id,
        questionTemplateId: null,
        order: order,
        questionType: qType,
        isExclusivePerRow: null,
        isRequired: true,
        imageUrl: null,
        requiredOptions: {
          strategy: GridRequiredStrategy.OnePerRow,
        },
        isLocked: false,
        randomizeColumn: false,
        randomizeRow: false,
        title: null,
        content: getNewTemplateContent(null),
        description: null,
        columns: createRatingStepsSchema(),
        rows: [createAnswerSchema(null, 0)],
      };
    case QuestionType.DropdownMenu:
    case QuestionType.RadioButtonList:
    case QuestionType.CheckboxList:
    case QuestionType.ContinuousSumList:
      return {
        id: id,
        questionTemplateId: null,
        order: order,
        questionType: qType,
        isRequired: true,
        imageUrl: null,
        requiredOptions: null,
        isLocked: false,
        randomize: false,
        title: null,
        content: getNewTemplateContent(null),
        description: null,
        answerOptions: [createAnswerSchema(null, 0)],
      };
    case QuestionType.RankingList:
      return {
        id: id,
        questionTemplateId: null,
        order: order,
        questionType: qType,
        isRequired: true,
        imageUrl: null,
        requiredOptions: null,
        isLocked: false,
        randomize: false,
        title: null,
        content: getNewTemplateContent(null),
        description: null,
        answerOptions: createRankingAnswerSchema(),
      };
    case QuestionType.TextboxGridNumeric:
      return {
        id: id,
        questionTemplateId: null,
        order: order,
        questionType: qType,
        isExclusivePerRow: null,
        isRequired: true,
        imageUrl: null,
        requiredOptions: {
          strategy: GridRequiredStrategy.All,
        },
        isLocked: false,
        randomizeColumn: false,
        randomizeRow: false,
        title: null,
        content: getNewTemplateContent(null),
        description: null,
        columns: [createAnswerSchema(null, 0), createAnswerSchema(null, 1)],
        rows: [createAnswerSchema(null, 0), createAnswerSchema(null, 1)],
      };
    case QuestionType.TextboxListText:
    case QuestionType.TextboxListNumeric:
      return {
        id: id,
        questionTemplateId: null,
        order: order,
        questionType: qType,
        isRequired: true,
        imageUrl: null,
        requiredOptions: {
          minAmount: 1,
        },
        isLocked: false,
        randomize: false,
        title: null,
        content: getNewTemplateContent(null),
        description: null,
        answerOptions: [createAnswerSchema(null, 0)],
      };
    default:
      //throw Error(
      //`Unknown QuestionType (${qType}) was used in question schema creator.`
      // );
      return null;
  }
}

export function createAnswerSchema(
  id: UUID | null,
  order: number,
  content?: TemplateContent,
  typ?: AnswerType
): AnswerColumnSchema | AnswerRowSchema {
  return {
    id: id ? id : uuid(),
    label: null,
    content: content ? content : getNewTemplateContent(null),
    order: order,
    typ: typ ? typ : AnswerType.Standard,
  };
}
export function createAnswerSchemaWithLabel(
  id: UUID | null,
  order: number,
  label: string | null,
  content?: TemplateContent,
  typ?: AnswerType
): AnswerColumnSchema | AnswerRowSchema {
  return { ...createAnswerSchema(id, order, content, typ), label: label };
}

export function createRatingStepsSchema(): AnswerColumnSchema[] {
  const length = 5;
  const labels = Array.from({ length }, (_v, index) => (index + 1).toString());
  return _.map(labels, (label, index) => {
    const contentText =
      index === 0 ? 'Low' : index === length - 1 ? 'High' : null;
    return {
      id: uuid(),
      label: label,
      content: getNewTemplateContent(contentText),
      order: index,
      typ: AnswerType.Standard,
    };
  });
}

export function createRankingAnswerSchema(): AnswerColumnSchema[] {
  const length = 3;
  const labels = Array.from({ length }, (_v, index) => (index + 1).toString());
  return _.map(labels, (label, index) => {
    return {
      id: uuid(),
      label: label,
      order: index,
      content: getNewTemplateContent(null),
      typ: AnswerType.Standard,
    };
  });
}

const getNewTemplateContent = (text: string | null) =>
  new TemplateContent('%%0', {
    0: {
      placeholder: 'Insert text',
      text: text,
    },
  });
